import { useAsyncData } from '#imports'
import { computed, toValue } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import {
  type BusinessDetails,
  type KybFile,
  type KybResponse,
  type KybStep,
  KybStepCode,
  KybStepStatus,
  type Stakeholder,
  type StakeholderInput,
  StakeholderRole,
} from '~/api/kyb/types'
import type { StakeholderFormValues } from '~/scopes/kyb/pages/components/steps/components/StakeholderForm/StakeholderForm.types'

import { transformStakeholderFormToApi } from '../utils/kyb.utils'

const KYB_DATA_KEY = 'kyb-data'

export function useKybData() {
  const $httpFetch = useHttpFetch()

  const {
    data: kybData,
    status,
    refresh: refreshKybData,
    execute: fetchKybData,
  } = useAsyncData<KybResponse>(
    KYB_DATA_KEY,
    () => $httpFetch('/api/payout-experience/v1/kyb'),
    {
      immediate: false,
    },
  )

  const isLoading = computed(() => status.value === 'pending')

  const steps = computed(() => kybData.value?.steps ?? [])
  const errors = computed(() => kybData.value?.errors ?? [])

  const businessDetails = computed(() => kybData.value?.data?.business)
  const stakeholders = computed(() => kybData.value?.data?.stakeholders ?? [])
  const letterOfAuthorization = computed(
    () => kybData.value?.data?.letterOfAuthorization,
  )

  const accountRequester = computed(() =>
    stakeholders.value.find((stakeholder) =>
      stakeholder.roles.includes(StakeholderRole.ACCOUNT_REQUESTER),
    ),
  )

  const legalRepresentative = computed(() =>
    stakeholders.value.find((stakeholder) =>
      stakeholder.roles.includes(StakeholderRole.LEGAL_REPRESENTATIVE),
    ),
  )

  const primaryBeneficialOwner = computed(() =>
    stakeholders.value.find((stakeholder) =>
      stakeholder.roles.includes(StakeholderRole.PRIMARY_BENEFICIAL_OWNER),
    ),
  )

  const secondaryBeneficialOwners = computed(() =>
    stakeholders.value.filter((stakeholder) =>
      stakeholder.roles.includes(StakeholderRole.SECONDARY_BENEFICIAL_OWNER),
    ),
  )

  async function saveBusinessDetails(business: BusinessDetails) {
    if (businessDetails.value) {
      await updateBusinessDetails(business)
    } else {
      await createBusinessDetails(business)
    }
  }

  async function updateBusinessDetails(business: BusinessDetails) {
    await $httpFetch('/api/payout-experience/v1/kyb/business-details', {
      method: 'PUT',
      body: business,
    })
  }

  async function createBusinessDetails(business: BusinessDetails) {
    await $httpFetch('/api/payout-experience/v1/kyb/business-details', {
      method: 'POST',
      body: business,
    })
  }

  async function updateStakeholder(stakeholder: StakeholderInput) {
    if (!stakeholder.id) {
      throw new Error('Stakeholder not found')
    }

    return $httpFetch('/api/payout-experience/v1/kyb/stakeholders/:id', {
      method: 'PUT',
      pathParams: { id: stakeholder.id },
      body: stakeholder,
    })
  }

  function createStakeholder(stakeholder: StakeholderInput) {
    return $httpFetch('/api/payout-experience/v1/kyb/stakeholders', {
      method: 'POST',
      body: stakeholder,
    })
  }

  async function saveStakeholder(
    formValues: Omit<
      StakeholderFormValues,
      'photoIdFrontFile' | 'photoIdBackFile'
    > & { photoIdFront?: KybFile; photoIdBack?: KybFile },
    roles: StakeholderRole[],
  ) {
    const stakeholderData = transformStakeholderFormToApi(
      formValues,
      roles,
      formValues.id,
    )

    return stakeholderData.id
      ? updateStakeholder(stakeholderData)
      : createStakeholder(stakeholderData)
  }

  async function addRolesToStakeholder(
    stakeholder: Stakeholder,
    roles: StakeholderRole[],
  ) {
    stakeholder.roles.push(...roles)

    await updateStakeholder(stakeholder)
  }

  async function deleteStakeholder(stakeholderId: string) {
    await $httpFetch('/api/payout-experience/v1/kyb/stakeholders/:id', {
      method: 'DELETE',
      pathParams: { id: stakeholderId },
    })
  }

  const isLegalRepresentativeSameAsAccountRequester = computed(() => {
    return legalRepresentative.value?.id === accountRequester.value?.id
  })

  const isBeneficialOwnerSameAsAccountRequester = computed(() => {
    return primaryBeneficialOwner.value?.id === accountRequester.value?.id
  })

  const needsLetterOfAuthorization = computed(() => {
    if (
      steps.value.find(
        ({ value }) => value === KybStepCode.LETTER_OF_AUTHORIZATION,
      )?.status === KybStepStatus.SKIP
    ) {
      return false
    }

    // As long as we don’t know if they are the same, we hide the step
    if (!primaryBeneficialOwner.value?.id || !accountRequester.value?.id) {
      return false
    }

    return isBeneficialOwnerSameAsAccountRequester
  })

  const isBeneficialOwnerSameAsLegalRepresentative = computed(() => {
    return primaryBeneficialOwner.value?.id === legalRepresentative.value?.id
  })

  async function submitKyb() {
    await $httpFetch('/api/payout-experience/v1/kyb/submit', {
      method: 'POST',
    })
  }

  /**
   * User cannot edit a step before having completed previous ones.
   * We count steps with errors as done, as this status is computed
   * asynchronously.
   */
  function isStepEditable(currentStepCode: KybStep['value']) {
    const stepList = toValue(steps)

    // Full form has been submitted: nothing is editable
    if (
      stepList.find(({ value }) => value === KybStepCode.REVIEW)?.status ===
      KybStepStatus.DONE
    ) {
      return false
    }

    const currentStepIndex = stepList.findIndex(
      ({ value }) => value === currentStepCode,
    )
    const previousSteps = stepList.slice(0, currentStepIndex)

    return previousSteps.every(
      ({ status: stepStatus }) => stepStatus !== KybStepStatus.TODO,
    )
  }

  return {
    steps,
    kybData,
    status,
    errors,
    businessDetails,
    stakeholders,
    letterOfAuthorization,
    accountRequester,
    legalRepresentative,
    primaryBeneficialOwner,
    secondaryBeneficialOwners,
    isLoading,
    fetchKybData,
    refreshKybData,
    saveBusinessDetails,
    updateStakeholder,
    createStakeholder,
    saveStakeholder,
    addRolesToStakeholder,
    deleteStakeholder,
    submitKyb,
    isLegalRepresentativeSameAsAccountRequester,
    isBeneficialOwnerSameAsAccountRequester,
    isBeneficialOwnerSameAsLegalRepresentative,
    workflow: {
      isStepEditable,
      needsLetterOfAuthorization,
    },
  }
}
